<template>
  <div class="kb-page">
    <AppHeader />

    <div v-if="post">
      <!-- Hero -->
      <section class="kb-blog-hero" :style="{ '--hero-img': `url(${post.coverImage})` }">
        <div class="kb-blog-hero__overlay"></div>
        <div class="container-xl kb-blog-hero__content" data-aos="fade-up">
          <div class="kb-page-hero__breadcrumb">
            <router-link to="/">Home</router-link>
            <i class="bi bi-chevron-right mx-2"></i>
            <router-link to="/blog">Blog</router-link>
            <i class="bi bi-chevron-right mx-2"></i>
            <span>{{ post.title }}</span>
          </div>
          <span class="kb-blog-hero__cat" :style="{ background: post.categoryColor }">{{ post.category }}</span>
          <h1>{{ post.title }}</h1>
          <div class="kb-blog-hero__meta">
            <span><i class="bi bi-person-circle me-1"></i>{{ post.author }}</span>
            <span><i class="bi bi-calendar3 me-1"></i>{{ post.date }}</span>
            <span><i class="bi bi-clock me-1"></i>{{ post.readTime }}</span>
          </div>
        </div>
      </section>

      <!-- Article -->
      <section class="kb-section kb-section--light">
        <div class="container-xl">
          <div class="kb-blog-layout">

            <!-- Main Article -->
            <article class="kb-blog-article">
              <template v-for="(block, i) in post.content" :key="i">

                <!-- Intro -->
                <p v-if="block.type === 'intro'" class="kb-article__intro">{{ block.text }}</p>

                <!-- Heading -->
                <h2 v-if="block.type === 'heading'" class="kb-article__heading">
                  <span class="kb-article__heading-line"></span>{{ block.text }}
                </h2>

                <!-- Paragraph -->
                <p v-if="block.type === 'paragraph'" class="kb-article__para">{{ block.text }}</p>

                <!-- Bullet List -->
                <ul v-if="block.type === 'bullet'" class="kb-article__list">
                  <li v-for="(item, li) in block.items" :key="li">
                    <i class="bi bi-check-circle-fill me-2"></i>{{ item }}
                  </li>
                </ul>

                <!-- Table -->
                <div v-if="block.type === 'table'" class="kb-article__table-wrap">
                  <table class="kb-article__table">
                    <thead>
                      <tr>
                        <th v-for="(h, hi) in block.headers" :key="hi">{{ h }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, ri) in block.rows" :key="ri">
                        <td v-for="(cell, ci) in row" :key="ci">{{ cell }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Conclusion -->
                <div v-if="block.type === 'conclusion'" class="kb-article__conclusion">
                  <i class="bi bi-lightbulb-fill me-2"></i>
                  <p>{{ block.text }}</p>
                </div>

              </template>

              <!-- Tags -->
              <div class="kb-article__tags">
                <span class="kb-article__tags-label">Tags:</span>
                <span v-for="tag in post.tags" :key="tag" class="kb-tag">#{{ tag }}</span>
              </div>

              <!-- Share -->
              <div class="kb-article__share">
                <span>Share this article:</span>
                <a :href="`https://wa.me/?text=${encodeURIComponent(post.title + ' – ' + post.excerpt + ' ' + pageUrl)}`" target="_blank" class="kb-share-btn kb-share-btn--wa">
                  <i class="bi bi-whatsapp"></i>
                </a>
                <a :href="linkedinShareUrl" target="_blank" class="kb-share-btn kb-share-btn--li">
                  <i class="bi bi-linkedin"></i>
                </a>
                <a :href="twitterShareUrl" target="_blank" class="kb-share-btn kb-share-btn--tw">
                  <i class="bi bi-twitter-x"></i>
                </a>
              </div>
            </article>

            <!-- Sidebar -->
            <aside class="kb-blog-sidebar">
              <!-- Author Card -->
              <div class="kb-sidebar-card">
                <div class="kb-author-card">
                  <div class="kb-author-card__avatar">
                    <i class="bi bi-people-fill"></i>
                  </div>
                  <div>
                    <p class="kb-author-card__name">{{ post.author }}</p>
                    <p class="kb-author-card__role">Industrial Packaging Experts</p>
                  </div>
                </div>
                <p class="kb-author-card__bio">
                  KeBee Industries is a Mumbai-based manufacturer of premium tarpaulins, nets, and ropes with a proven track record. Our team shares expert knowledge to help you make informed industrial purchasing decisions.
                </p>
              </div>

              <!-- Other Posts -->
              <div class="kb-sidebar-card">
                <h4 class="kb-sidebar-card__title">More Articles</h4>
                <div class="kb-sidebar-posts">
                  <router-link
                    v-for="other in otherPosts"
                    :key="other.id"
                    :to="`/blog/${other.slug}`"
                    class="kb-sidebar-post"
                  >
                    <img :src="other.coverImage" :alt="other.title" loading="lazy" />
                    <div>
                      <p>{{ other.title }}</p>
                      <span>{{ other.readTime }}</span>
                    </div>
                  </router-link>
                </div>
              </div>

              <!-- CTA Card -->
              <div class="kb-sidebar-card kb-sidebar-cta">
                <h4>Need Industrial Packaging?</h4>
                <p>Get a custom quote tailored to your requirements.</p>
                <a href="https://wa.me/919892310123" target="_blank" class="kb-btn kb-btn--primary d-block text-center">
                  <i class="bi bi-whatsapp me-2"></i>Get a Quote
                </a>
              </div>
            </aside>

          </div>
        </div>
      </section>

      <!-- Related Blogs -->
      <section class="kb-section" v-if="relatedPosts.length">
        <div class="container-xl">
          <div class="kb-section-header mb-5" data-aos="fade-up">
            <span class="kb-section-header__tag">Read More</span>
            <h2>Related Articles</h2>
          </div>
          <div class="kb-related-grid">
            <div v-for="related in relatedPosts" :key="related.id" class="kb-blog-card-sm" data-aos="fade-up">
              <div class="kb-blog-card-sm__img">
                <img :src="related.coverImage" :alt="related.title" loading="lazy" />
                <span class="kb-blog-card-sm__cat" :style="{ background: related.categoryColor }">{{ related.category }}</span>
              </div>
              <div class="kb-blog-card-sm__body">
                <div class="kb-blog-card-sm__meta">
                  <span><i class="bi bi-person-circle me-1"></i>{{ related.author }}</span>
                  <span><i class="bi bi-clock me-1"></i>{{ related.readTime }}</span>
                </div>
                <h4>{{ related.title }}</h4>
                <p>{{ related.excerpt }}</p>
                <router-link :to="`/blog/${related.slug}`" class="kb-btn kb-btn--navy kb-btn--sm">
                  Read Article <i class="bi bi-arrow-right ms-1"></i>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- 404 -->
    <div v-else class="kb-notfound">
      <div class="container-xl text-center py-5">
        <i class="bi bi-file-earmark-x kb-notfound__icon"></i>
        <h2>Article Not Found</h2>
        <p class="text-muted">The blog post you're looking for doesn't exist or has been removed.</p>
        <router-link to="/blog" class="kb-btn kb-btn--navy">
          <i class="bi bi-arrow-left me-2"></i>Back to Blog
        </router-link>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import { setSeo } from '@/utils/seo.js'
import { blogs, getBlogBySlug } from '@/data/blogs.js'

export default {
  name: 'BlogDetailPage',
  components: { AppHeader, AppFooter },
  data() {
    return { post: null }
  },
  computed: {
    otherPosts() {
      return blogs.filter(b => b.slug !== this.post?.slug).slice(0, 3)
    },
    relatedPosts() {
      if (!this.post) return []
      return blogs.filter(b => b.slug !== this.post.slug && b.category === this.post.category).slice(0, 3)
    },
    pageUrl() {
      return typeof window !== 'undefined' ? window.location.href : ''
    },
    linkedinShareUrl() {
      const url = encodeURIComponent(this.pageUrl)
      const title = encodeURIComponent(this.post.title)
      const summary = encodeURIComponent(this.post.excerpt)
      return `https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${title}&summary=${summary}&source=KeBee%20Industries`
    },
    twitterShareUrl() {
      const url = encodeURIComponent(this.pageUrl)
      const text = encodeURIComponent(this.post.title)
      const tags = (this.post.tags || []).slice(0, 3).join(',')
      return `https://twitter.com/intent/tweet?text=${text}&url=${url}&hashtags=${encodeURIComponent(tags)}&via=KeBeeIndustries`
    }
  },
  methods: {
    loadPost(slug) {
      this.post = getBlogBySlug(slug)
      if (this.post) {
        const absImage = typeof window !== 'undefined' && this.post.coverImage
          ? window.location.origin + (this.post.coverImage.startsWith('/') ? '' : '/') + this.post.coverImage.replace(/^.*\/img\//, '/img/')
          : ''
        setSeo({
          title: `${this.post.title} | KeBee Industries Blog`,
          description: this.post.excerpt,
          path: `/blog/${this.post.slug}`,
          image: absImage || undefined,
          keywords: this.post.tags.join(', ') + ', KeBee Industries'
        })
      }
    }
  },
  watch: {
    '$route.params.slug'(newSlug) {
      this.loadPost(newSlug)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  },
  created() {
    this.loadPost(this.$route.params.slug)
  }
}
</script>

<style scoped>
/* Hero */
.kb-blog-hero {
  min-height: 480px;
  position: relative;
  display: flex;
  align-items: flex-end;
  padding-bottom: 60px;
}
.kb-blog-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background: var(--hero-img) center/cover no-repeat;
  opacity: 0.2;
}
.kb-blog-hero__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, #0F1F3D 50%, rgba(15,31,61,0.6) 100%);
}
.kb-blog-hero__content {
  position: relative;
  z-index: 2;
  padding-top: 120px;
  max-width: 820px;
}
.kb-page-hero__breadcrumb {
  display: flex;
  align-items: center;
  font-size: 0.82rem;
  color: rgba(255,255,255,0.55);
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}
.kb-page-hero__breadcrumb a { color: rgba(255,255,255,0.55); text-decoration: none; }
.kb-page-hero__breadcrumb a:hover { color: #F59E0B; }
.kb-blog-hero__cat {
  display: inline-block;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 5px 16px;
  border-radius: 50px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 1rem;
}
.kb-blog-hero__content h1 {
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.6rem, 3.5vw, 2.8rem);
  font-weight: 800;
  color: #fff;
  line-height: 1.25;
  margin-bottom: 1.25rem;
}
.kb-blog-hero__meta {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  color: rgba(255,255,255,0.65);
  font-size: 0.85rem;
}

/* Layout */
.kb-blog-layout {
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 3rem;
  align-items: start;
}

/* Article */
.kb-blog-article {
  background: #fff;
  border-radius: 20px;
  padding: 2.5rem;
  box-shadow: 0 4px 24px rgba(15,31,61,0.07);
}
.kb-article__intro {
  font-size: 1.1rem;
  color: #374151;
  line-height: 1.8;
  font-weight: 500;
  border-left: 4px solid #F59E0B;
  padding-left: 1.5rem;
  margin-bottom: 2rem;
}
.kb-article__heading {
  font-family: 'Poppins', sans-serif;
  font-size: 1.3rem;
  font-weight: 700;
  color: #0F1F3D;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 2.25rem 0 1rem;
}
.kb-article__heading-line {
  display: inline-block;
  width: 4px;
  height: 24px;
  background: #F59E0B;
  border-radius: 2px;
  flex-shrink: 0;
}
.kb-article__para {
  color: #4B5563;
  line-height: 1.85;
  margin-bottom: 1.25rem;
  font-size: 0.95rem;
}
.kb-article__list {
  list-style: none;
  padding: 0;
  margin-bottom: 1.5rem;
}
.kb-article__list li {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 8px 0;
  border-bottom: 1px solid #F3F4F6;
  color: #374151;
  font-size: 0.93rem;
  line-height: 1.6;
}
.kb-article__list li:last-child { border-bottom: none; }
.kb-article__list .bi { color: #10B981; margin-top: 2px; flex-shrink: 0; }

.kb-article__table-wrap {
  overflow-x: auto;
  margin: 1.5rem 0;
  border-radius: 12px;
  box-shadow: 0 0 0 1px #E5E7EB;
}
.kb-article__table {
  width: 100%;
  border-collapse: collapse;
}
.kb-article__table thead th {
  background: #0F1F3D;
  color: #fff;
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 12px 16px;
  text-align: left;
}
.kb-article__table tbody tr:nth-child(even) { background: #F9FAFB; }
.kb-article__table tbody tr:hover { background: #EFF6FF; }
.kb-article__table tbody td {
  padding: 11px 16px;
  font-size: 0.875rem;
  color: #374151;
  border-bottom: 1px solid #E5E7EB;
}

.kb-article__conclusion {
  background: linear-gradient(135deg, #FEF3C7, #FDE68A);
  border-left: 4px solid #F59E0B;
  border-radius: 0 12px 12px 0;
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-top: 2rem;
}
.kb-article__conclusion .bi { color: #D97706; font-size: 1.25rem; margin-top: 2px; flex-shrink: 0; }
.kb-article__conclusion p { margin: 0; color: #92400E; font-size: 0.95rem; line-height: 1.7; font-weight: 500; }

.kb-article__tags {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 2.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #E5E7EB;
}
.kb-article__tags-label { font-weight: 600; font-size: 0.85rem; color: #6B7280; }
.kb-tag {
  font-size: 0.72rem;
  background: #F3F4F6;
  color: #6B7280;
  padding: 4px 12px;
  border-radius: 50px;
  font-weight: 500;
}

.kb-article__share {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1.25rem;
  font-size: 0.85rem;
  color: #6B7280;
}
.kb-share-btn {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1rem;
  text-decoration: none;
  transition: transform 0.2s, opacity 0.2s;
}
.kb-share-btn:hover { transform: scale(1.1); opacity: 0.9; color: #fff; }
.kb-share-btn--wa { background: #25D366; }
.kb-share-btn--li { background: #0A66C2; }
.kb-share-btn--tw { background: #000; }

/* Sidebar */
.kb-blog-sidebar { display: flex; flex-direction: column; gap: 1.5rem; }
.kb-sidebar-card {
  background: #fff;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 24px rgba(15,31,61,0.07);
}
.kb-sidebar-card__title {
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: #0F1F3D;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #F59E0B;
}

.kb-author-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}
.kb-author-card__avatar {
  width: 52px;
  height: 52px;
  background: linear-gradient(135deg, #0F1F3D, #1E3A6E);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  color: #F59E0B;
  flex-shrink: 0;
}
.kb-author-card__name { font-weight: 700; color: #0F1F3D; font-size: 0.95rem; margin: 0; }
.kb-author-card__role { font-size: 0.78rem; color: #9CA3AF; margin: 0; }
.kb-author-card__bio { font-size: 0.83rem; color: #6B7280; line-height: 1.65; margin: 0; }

.kb-sidebar-posts { display: flex; flex-direction: column; gap: 1rem; }
.kb-sidebar-post {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  text-decoration: none;
  padding: 8px;
  border-radius: 10px;
  transition: background 0.2s;
}
.kb-sidebar-post:hover { background: #F9FAFB; }
.kb-sidebar-post img {
  width: 68px;
  height: 52px;
  object-fit: cover;
  border-radius: 8px;
  flex-shrink: 0;
}
.kb-sidebar-post p { margin: 0; font-size: 0.82rem; font-weight: 600; color: #1F2937; line-height: 1.4; }
.kb-sidebar-post span { font-size: 0.73rem; color: #9CA3AF; }

.kb-sidebar-cta { background: linear-gradient(135deg, #0F1F3D 0%, #1E3A6E 100%); }
.kb-sidebar-cta h4 { font-family: 'Poppins', sans-serif; font-size: 1rem; font-weight: 700; color: #fff; margin-bottom: 0.5rem; }
.kb-sidebar-cta p { font-size: 0.85rem; color: rgba(255,255,255,0.7); margin-bottom: 1.25rem; }

/* Related */
.kb-related-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.75rem;
}
.kb-blog-card-sm {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(15,31,61,0.07);
  transition: transform 0.3s, box-shadow 0.3s;
}
.kb-blog-card-sm:hover { transform: translateY(-4px); box-shadow: 0 12px 40px rgba(15,31,61,0.12); }
.kb-blog-card-sm__img { position: relative; height: 180px; overflow: hidden; }
.kb-blog-card-sm__img img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.3s; }
.kb-blog-card-sm:hover .kb-blog-card-sm__img img { transform: scale(1.05); }
.kb-blog-card-sm__cat {
  position: absolute;
  top: 12px;
  left: 12px;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 50px;
  text-transform: uppercase;
}
.kb-blog-card-sm__body { padding: 1.25rem; }
.kb-blog-card-sm__meta { display: flex; gap: 0.75rem; font-size: 0.75rem; color: #9CA3AF; margin-bottom: 0.6rem; flex-wrap: wrap; }
.kb-blog-card-sm__body h4 { font-family: 'Poppins', sans-serif; font-size: 0.95rem; font-weight: 700; color: #0F1F3D; margin-bottom: 0.5rem; line-height: 1.4; }
.kb-blog-card-sm__body p { font-size: 0.83rem; color: #6B7280; margin-bottom: 1rem; line-height: 1.65; }

/* 404 */
.kb-notfound { padding: 100px 0 60px; }
.kb-notfound__icon { font-size: 4rem; color: #D1D5DB; display: block; margin-bottom: 1rem; }
.kb-notfound h2 { font-family: 'Poppins', sans-serif; color: #0F1F3D; margin-bottom: 0.5rem; }

@media (max-width: 1024px) {
  .kb-blog-layout { grid-template-columns: 1fr; }
  .kb-blog-sidebar { order: -1; display: grid; grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 768px) {
  .kb-related-grid { grid-template-columns: 1fr; }
  .kb-blog-sidebar { grid-template-columns: 1fr; }
  .kb-blog-article { padding: 1.5rem; }
  .kb-article__share { flex-wrap: wrap; gap: 10px; }
}
@media (max-width: 576px) {
  .kb-blog-article { padding: 1rem; }
  .kb-article__table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .kb-blog-hero h1 { font-size: clamp(1.4rem, 6vw, 2rem); }
}
</style>
